import axiosInstance from './axios';

function projectPath(projectId, suffix) {
    return `/transition/projects/${projectId}/${suffix}`;
}

export function transitionErrorCode(error) {
    const code = error?.response?.data?.code;
    return typeof code === 'string' ? code : '';
}

const UPLOAD_PARSE_ERROR_CODES = new Set([
    'too_many_rows',
    'too_many_sheets',
    'empty_file',
    'unsupported_type',
    'corrupt_file',
    'password_protected',
    'parse_failed',
    'file_too_large',
    'file_required',
]);

export function isUploadParseError(error) {
    return UPLOAD_PARSE_ERROR_CODES.has(transitionErrorCode(error));
}

export function transitionErrorMessage(error) {
    const data = error?.response?.data;
    if (typeof data?.detail === 'string') return data.detail;
    if (typeof data === 'string' && data) {
        if (data.includes('<!DOCTYPE') || data.includes('<html')) {
            return 'The server returned an unexpected error. Restart the import step and try again.';
        }
        return data;
    }
    if (error?.message) return error.message;
    return 'Transition could not complete that request.';
}

export function getSummary(projectId) {
    return axiosInstance.get(projectPath(projectId, 'summary/'));
}

export function listRecords(projectId, params) {
    return axiosInstance.get(projectPath(projectId, 'records/'), { params });
}

export function getRecord(projectId, recordId) {
    return axiosInstance.get(projectPath(projectId, `records/${recordId}/`));
}

export function createRecord(projectId, body) {
    return axiosInstance.post(projectPath(projectId, 'records/'), body);
}

export function patchRecord(projectId, recordId, body) {
    return axiosInstance.patch(projectPath(projectId, `records/${recordId}/`), body);
}

export function bulkUpdate(projectId, body) {
    return axiosInstance.post(projectPath(projectId, 'records/bulk/'), body);
}

export function listViews(projectId) {
    return axiosInstance.get(projectPath(projectId, 'views/'));
}

export function getFilters(projectId) {
    return axiosInstance.get(projectPath(projectId, 'filters/'));
}

export function pastePreview(projectId, body) {
    return axiosInstance.post(projectPath(projectId, 'paste/preview/'), body);
}

export function pasteCommit(projectId, body) {
    return axiosInstance.post(projectPath(projectId, 'paste/commit/'), body);
}

export function listImports(projectId) {
    return axiosInstance.get(projectPath(projectId, 'imports/'));
}

export function createImport(projectId, body) {
    return axiosInstance.post(projectPath(projectId, 'imports/'), body);
}

export function getImport(projectId, batchId) {
    return axiosInstance.get(projectPath(projectId, `imports/${batchId}/`));
}

export function uploadImportFile(projectId, batchId, file) {
    const form = new FormData();
    form.append('file', file);
    return axiosInstance.post(projectPath(projectId, `imports/${batchId}/files/`), form);
}

export function deleteImportFile(projectId, batchId, snapshotId) {
    return axiosInstance.delete(projectPath(projectId, `imports/${batchId}/files/${snapshotId}/`));
}

export function saveImportDraft(projectId, batchId, body) {
    return axiosInstance.patch(projectPath(projectId, `imports/${batchId}/draft/`), body);
}

export function saveImportMappings(projectId, batchId, body) {
    return axiosInstance.put(projectPath(projectId, `imports/${batchId}/mappings/`), body);
}

export function suggestImportAiMapping(projectId, batchId, body = {}) {
    return axiosInstance.post(projectPath(projectId, `imports/${batchId}/ai-mapping/`), body);
}

export function getImportWorksheet(projectId, batchId, sheetId) {
    return axiosInstance.get(projectPath(projectId, `imports/${batchId}/worksheets/${sheetId}/`));
}

export function previewImport(projectId, batchId) {
    return axiosInstance.post(projectPath(projectId, `imports/${batchId}/preview/`));
}

export function saveImportDecisions(projectId, batchId, body) {
    return axiosInstance.put(projectPath(projectId, `imports/${batchId}/decisions/`), body);
}

export function commitImport(projectId, batchId, body) {
    return axiosInstance.post(projectPath(projectId, `imports/${batchId}/commit/`), body);
}

export function cancelImport(projectId, batchId) {
    return axiosInstance.post(projectPath(projectId, `imports/${batchId}/cancel/`));
}

export function exportRecords(projectId, body) {
    return axiosInstance.post(projectPath(projectId, 'exports/'), body, { responseType: 'blob' });
}

export function getPreferences(projectId, viewKey) {
    return axiosInstance.get(projectPath(projectId, 'preferences/'), { params: { view_key: viewKey || 'all' } });
}

export function savePreferences(projectId, body) {
    return axiosInstance.put(projectPath(projectId, 'preferences/'), body);
}
